import { useEffect, useState } from 'react';
import Spinner from 'components/crm/ui/Spinner';
import { useToast } from 'components/crm/ui/Toast';
import CallList from 'components/prospection/CallList';
import CallListBanner from 'components/prospection/CallListBanner';
import GlobalListPanel from 'components/prospection/GlobalListPanel';
import SessionHistory from 'components/prospection/SessionHistory';
import SessionSummary from 'components/prospection/SessionSummary';
import { apiErrorText, isCanceledRequest } from 'helper/apiErrorText';
import useText from 'hooks/useText';
import { upcomingCallbacks } from 'pages/prospection/callListItems';
import { getSalesRepOverview } from 'server/crm/prospectionApi';

/**
 * A Commercial's prospection as the agency manager sees it, read-only: his
 * day list (with its « Relances planifiées »), his global list and his
 * sessions, drawn with the page's own components but given no handler, so
 * that nothing is called, added, removed, moved or confirmed. Nothing here
 * opens a call sheet: no keyboard shortcut is listened to. `owner`: the
 * Commercial picked, { id, firstname, lastname }.
 */
const SalesRepOverview = ({ owner }) => {
  const { text, language } = useText('prospection');
  const toast = useToast();
  // { data, now }: the answer, and when it was read ("today" for the labels).
  const [overview, setOverview] = useState(null);
  const [failed, setFailed] = useState(false);
  const [summary, setSummary] = useState(null);
  const ownerId = owner?.id;

  useEffect(() => {
    let active = true;
    setOverview(null);
    setFailed(false);
    setSummary(null);
    getSalesRepOverview({ ownerId }).then(
      (data) => {
        if (active) setOverview({ data, now: new Date() });
      },
      (failure) => {
        if (!active || isCanceledRequest(failure)) return;
        setFailed(true);
        toast(apiErrorText(failure, { language, text, group: 'errors' }), 'error');
      },
    );
    return () => {
      active = false;
    };
  }, [language, ownerId, text, toast]);

  const firstname =
    overview?.data?.owner?.firstname || owner?.firstname || '';
  const name = [owner?.firstname, owner?.lastname].filter(Boolean).join(' ');
  const readOnlyNotice = (
    <p className="mb-4 text-sm leading-5 text-muted">
      {text('overview.readOnly', { name })}
    </p>
  );

  if (!overview) {
    return (
      <div
        data-testid="sales-rep-overview"
        className="flex min-h-0 flex-1 flex-col px-8 pb-6 pt-6"
      >
        {readOnlyNotice}
        {!failed && (
          <div
            data-testid="sales-rep-overview-loading"
            className="flex min-h-[240px] items-center justify-center"
          >
            <Spinner />
          </div>
        )}
      </div>
    );
  }

  const { data, now } = overview;
  const day = data?.day ?? null;
  const upcoming = upcomingCallbacks(data?.prospects, now);

  return (
    <div
      data-testid="sales-rep-overview"
      className="flex min-h-0 flex-1 gap-6 px-8 pb-6 pt-6"
    >
      <section className="flex min-h-0 w-[560px] shrink-0 flex-col">
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
          {readOnlyNotice}
          {day ? (
            <>
              <div className="mb-4">
                <CallListBanner day={day} editing={false} />
              </div>
              <CallList
                day={day}
                loading={false}
                error={null}
                now={now}
                upcoming={upcoming}
                simple
              />
            </>
          ) : (
            <p
              data-testid="day-not-opened"
              className="py-4 text-sm leading-5 text-body"
            >
              {text('overview.notOpened', { firstname })}
            </p>
          )}
          <div className="mt-6">
            <GlobalListPanel
              inline
              prospects={data?.globalList ?? []}
              loading={false}
              counts={day?.counts}
            />
          </div>
        </div>
      </section>
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        {summary ? (
          <SessionSummary
            session={summary}
            justEnded={false}
            onBack={() => setSummary(null)}
          />
        ) : (
          <SessionHistory
            sessions={Array.isArray(data?.sessions) ? data.sessions : []}
            onOpen={setSummary}
            title={text('overview.sessionsTitle', { firstname })}
            emptyText={text('overview.noSession', { firstname })}
          />
        )}
      </section>
    </div>
  );
};

export default SalesRepOverview;
